import { useState } from 'react';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';

export function WaitlistSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setStatus('error');
      setErrorMessage('Please enter your email address.');
      return;
    }

    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    // Store in localStorage for MVP
    const waitlist = JSON.parse(localStorage.getItem('positioniq_waitlist') || '[]');
    if (waitlist.includes(email)) {
      setStatus('error');
      setErrorMessage('This email is already on the waitlist.');
      return;
    }

    waitlist.push(email);
    localStorage.setItem('positioniq_waitlist', JSON.stringify(waitlist));

    console.log('Waitlist signup:', email);
    setStatus('success');
    setEmail('');
  };

  return (
    <section id="waitlist" className="relative py-20 md:py-28 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
      {/* Dot pattern overlay */}
      <div className="absolute inset-0 opacity-20" aria-hidden="true">
        <svg className="absolute inset-0 h-full w-full">
          <defs>
            <pattern id="waitlist-dots" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#waitlist-dots)" />
        </svg>
      </div>

      <div className="container relative">
        <div className="mx-auto max-w-2xl">
          <div className="rounded-3xl bg-white/5 border border-white/10 p-8 md:p-12 text-center">
            <div className="inline-flex rounded-full bg-purple-600 p-4 mb-6">
              <Mail className="h-8 w-8 text-white" />
            </div>

            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl mb-4 text-white">
              Join the Waitlist
            </h2>
            <p className="text-white/80 mb-8 max-w-lg mx-auto">
              Be the first to know when PositionIQ launches. Get early access and exclusive updates.
            </p>

            {status === 'success' ? (
              <div className="flex items-center justify-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-4">
                <CheckCircle className="h-5 w-5 text-emerald-400" />
                <span className="font-medium text-emerald-300">You're on the list! We'll be in touch soon.</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <div className="flex-1">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setStatus('idle');
                    }}
                    className="w-full rounded-xl border-2 border-slate-700 bg-slate-800 px-4 py-3.5 text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:bg-slate-800/80 transition-colors"
                  />
                  {status === 'error' && (
                    <div className="flex items-center gap-1 mt-2 text-sm text-red-300">
                      <AlertCircle className="h-4 w-4" />
                      <span>{errorMessage}</span>
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="rounded-xl bg-purple-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-purple-500 transition-colors whitespace-nowrap"
                >
                  Join Waitlist
                </button>
              </form>
            )}

            <p className="mt-6 text-xs text-white/50">
              No spam. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
